import { useEffect, useCallback } from "react"
import { useLocation } from "react-router-dom"

export const useScrollToHash = () => {
  const location = useLocation()

  const scrollToElement = useCallback(
    (hash: string = location.hash) => {
      const HEADER_OFFSET = 120 // ~7.5rem to account for fixed header

      // Small timeout to ensure the DOM is ready
      setTimeout(() => {
        if (hash) {
          const element = document.querySelector(hash)
          if (element) {
            const elementPosition =
              element.getBoundingClientRect().top + window.scrollY
            window.scrollTo({
              top: elementPosition - HEADER_OFFSET,
              behavior: "smooth",
            })
          }
        } else {
          window.scrollTo({ top: 0, behavior: "smooth" })
        }
      }, 0)
    },
    [location.hash],
  )

  useEffect(() => {
    // Handle initial load and route changes
    scrollToElement()

    // Handle browser back/forward buttons
    window.addEventListener("popstate", () => scrollToElement())

    // Handle clicks on hash links
    const handleHashLinkClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement
      const anchor = target.closest("a")

      if (anchor?.hash && anchor.pathname === location.pathname) {
        // If clicking a hash link on the same page
        e.preventDefault()
        scrollToElement(anchor.hash)

        // Update URL without adding to history
        window.history.replaceState(null, "", anchor.hash)
      }
    }

    document.addEventListener("click", handleHashLinkClick)

    // Cleanup
    return () => {
      window.removeEventListener("popstate", () => scrollToElement())
      document.removeEventListener("click", handleHashLinkClick)
    }
  }, [scrollToElement, location.pathname])
}
